import { Badge, Box, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useState } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import Divider from "@mui/material/Divider";
import Logout from "@mui/icons-material/Logout";
import { useLocation, useHistory } from "react-router-dom";

import NavListDrawer from "./NavListDrawer";
import {
  Drawer,
  Theme,
  Tooltip,
  IconButton,
  Avatar,
  Menu,
  MenuList,
  Button
} from "@mui/material";

import { useUserState } from "../../state/user";
import CmdBalance from "../CmdBalance/CmdBalance";
import { useAuth0 } from "@auth0/auth0-react";
import MenuItemComponent from "./MenuItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";
import {
  faCartShopping,
  faCircleDollarToSlot,
  faHouseUser,
  faPrescription,
  faUser,
  faUserDoctor
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { PAYMENTS_HOME_PATH } from "../../utils/paymentsRoutes";

const useStyles = makeStyles((theme: Theme) => ({
  avatar: {
    width: "120px"
  },
  logo: {
    height: "50px",
    width: "auto",
    cursor: "pointer",
    display: "block",
    [theme.breakpoints.down("sm")]: {
      height: "40px"
    }
  }
}));
/* Profile menu header: the professional's name on the left, the remaining CMD
   balance pushed to the right. */
const menuHeaderSx = {
  px: 2.5,
  pt: 1.5,
  pb: 1.25,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  borderBottom: `1px solid ${semanticColors.border.light}`
};

const menuHeaderNameSx = {
  ...typography.body.lBold,
  color: semanticColors.text.primary
};

const withouSidebarRoutes = ["/app/state/disconnected"];

export default function Navbar() {
  const history = useHistory();
  const { user } = useUserState();
  const location = useLocation();
  const { logout } = useAuth0();
  const { t, i18n } = useTranslation();

  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const navLinks = [
    {
      title: t("nav.home"),
      path: "/",
      icon: <FontAwesomeIcon icon={faHouseUser} size="lg" />
    },
    {
      title: t("nav.purchases"),
      path: "/cmds/buy",
      icon: <FontAwesomeIcon icon={faCartShopping} size="lg" />
    },
    {
      title: t("nav.signPrescriptions"),
      fn: () => (window.location.href = "https://sign.myscribo.com/"),
      icon: (
        <FontAwesomeIcon
          icon={faPrescription}
          size="lg"
          style={{ marginLeft: "2px" }}
        />
      )
    },
    {
      title: t("nav.paymentsAccount"),
      path: PAYMENTS_HOME_PATH,
      icon: <FontAwesomeIcon icon={faCircleDollarToSlot} size="lg" />
    }
  ];

  const profileLinks = [
    {
      title: t("nav.personalData"),
      path: "/personal-data",
      icon: <FontAwesomeIcon icon={faUser} size="lg" />
    },
    {
      title: t("nav.professionalData"),
      path: "/professional-data",
      icon: <FontAwesomeIcon icon={faUserDoctor} size="lg" />
    }
  ];

  const navLinksBottom = [
    {
      title: t("nav.logout"),
      icon: <Logout />,
      fn: () =>
        logout({
          logoutParams: {
            returnTo: window.location.origin
          }
        })
    }
  ];

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const getDoctorDisplayName = () => {
    const gender = user?.personalInfo?.gender;
    const title =
      gender === 1
        ? t("home.titleMale")
        : gender === 2
        ? t("home.titleFemale")
        : "";
    return title ? `${title} ${user?.clinicalName}` : user?.clinicalName ?? "";
  };

  const isPortuguese = i18n.language.startsWith("pt");

  const toggleLanguage = () => {
    i18n.changeLanguage(isPortuguese ? "en" : "pt");
  };

  if (!user) return <></>;

  if (withouSidebarRoutes.some(item => location.pathname.includes(item)))
    return null;

  const isHome = location.pathname === "/";

  return (
    <>
      <Box
        component="header"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1100,
          background: isHome ? "transparent" : semanticColors.gradient.surface,
          height: "80px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: "16px", sm: "24px" }
        }}
      >
        {/* Left: mobile menu + logo */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ display: { xs: "flex", sm: "none" } }}>
            <IconButton
              size="medium"
              onClick={() => setOpen(true)}
              sx={{ color: "white", p: 0.5 }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
          <img
            className={classes.logo}
            src="/logo_scribo.png"
            alt="Scribo"
            onClick={() => history.push("/")}
          />
        </Box>

        {/* Right: language + user avatar + menu */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            onClick={toggleLanguage}
            sx={{
              color: "white",
              fontWeight: "bold",
              fontSize: "0.85rem",
              minWidth: "auto",
              px: 1.5,
              border: "1px solid rgba(255,255,255,0.5)",
              borderRadius: 1
            }}
          >
            <img
              src={
                isPortuguese
                  ? "https://flagcdn.com/w20/gb.png"
                  : "https://flagcdn.com/w20/pt.png"
              }
              alt=""
              style={{
                width: 20,
                height: "auto",
                marginRight: 6,
                verticalAlign: "middle"
              }}
            />
            {isPortuguese ? "EN" : "PT"}
          </Button>
          <Tooltip title={t("nav.openMenu")}>
            <IconButton onClick={handleOpenUserMenu} size="large">
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                badgeContent={
                  <Avatar
                    sx={{
                      bgcolor: semanticColors.surface.canvas,
                      width: 24,
                      height: 24
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "3px",
                        "& span": {
                          display: "block",
                          width: 14,
                          height: 1.2,
                          bgcolor: semanticColors.highlight.default,
                          borderRadius: "0.5px"
                        }
                      }}
                    >
                      <span />
                      <span />
                    </Box>
                  </Avatar>
                }
              >
                <Avatar
                  alt={user?.personalInfo?.givenName}
                  src={user?.personalInfo?.picture}
                  sx={{
                    width: 56,
                    height: 56,
                    border: `2px solid ${semanticColors.highlight.default}`
                  }}
                />
              </Badge>
            </IconButton>
          </Tooltip>
          <Menu
            sx={{ mt: "60px" }}
            id="menu-appbar"
            anchorEl={anchorElUser}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            keepMounted
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 1.5,
                  borderRadius: "24px",
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1
                  },
                  "&:before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    right: 17,
                    width: 10,
                    height: 10,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0
                  }
                }
              }
            }}
          >
            <MenuList sx={{ pt: 0 }}>
              <Box sx={menuHeaderSx}>
                <Typography sx={menuHeaderNameSx}>
                  {getDoctorDisplayName()}
                </Typography>

                <CmdBalance />
              </Box>
              {profileLinks.map((item: any) => (
                <MenuItemComponent
                  key={item.title}
                  onClick={handleCloseUserMenu}
                  item={item}
                />
              ))}
              <Divider />
              {navLinks.map((item: any) => (
                <MenuItemComponent
                  key={item.title}
                  onClick={handleCloseUserMenu}
                  item={item}
                />
              ))}
              <Divider />
              {navLinksBottom.map((item: any) => (
                <MenuItemComponent
                  key={item.title}
                  onClick={handleCloseUserMenu}
                  item={item}
                />
              ))}
            </MenuList>
          </Menu>
        </Box>
      </Box>

      <Drawer
        open={open}
        anchor="left"
        onClose={() => setOpen(false)}
        sx={{ display: { xs: "block", sm: "none" } }}
      >
        <NavListDrawer
          navLinks={navLinks}
          navLinksBottom={navLinksBottom}
          onClose={() => setOpen(false)}
          user={user}
        />
      </Drawer>
    </>
  );
}
